import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useTheme } from '../contexts/ThemeContext';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';
import { useFieldShellDisplay } from '../contexts/DisplayPreferencesContext';

// Types
export type DateTimePickerMode = 'date' | 'time' | 'datetime';
export type DateTimePickerSelectionType = 'single' | 'range';
export type TimeQualifier = 'at' | 'before' | 'after';

export interface DateTimeValue {
  date?: Date;
  startDate?: Date;
  endDate?: Date;
  startTime?: string;
  endTime?: string;
  timeQualifier?: TimeQualifier;
  startTimeQualifier?: TimeQualifier;
  endTimeQualifier?: TimeQualifier;
}

export type DateTimePickerLabelType = 'auto' | 'date' | 'datetime' | 'date-range' | 'datetime-range' | 'time' | 'time-range' | 'custom' | 'none';
export type DateTimePickerOpenDirection = 'down' | 'up' | 'left' | 'right' | 'downleft' | 'downright' | 'upleft' | 'upright';

export type DateTimePickerInputVariant = 'default' | 'compact' | 'modal';

export interface DateTimePickerProps {
  mode?: DateTimePickerMode;
  selectionType?: DateTimePickerSelectionType;
  value?: Date | DateTimeValue | null;
  onChange?: (value: Date | DateTimeValue | null) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  minuteStep?: number;
  use24Hour?: boolean;
  className?: string;
  /** Label type: 'auto' generates based on mode/selectionType, or use specific types, 'custom' for custom label text, 'none' to hide */
  labelType?: DateTimePickerLabelType;
  /** Custom label text (only used when labelType is 'custom') */
  label?: string;
  error?: string;
  required?: boolean;
  /** Enable Before/After qualifier selection for time picker */
  showTimeQualifier?: boolean;
  /** Auto-apply and close when a single date is selected (only works with mode='date' and selectionType='single') */
  autoApply?: boolean;
  /** Direction the dropdown opens relative to the input (default: 'down') */
  openDirection?: DateTimePickerOpenDirection;
  /** Render dropdown in a portal to avoid clipping by parent containers */
  usePortal?: boolean;
  /** Custom portal container element (e.g. a dialog element). When provided, implies usePortal. */
  portalContainer?: HTMLElement | null;
  /** @deprecated Use inputVariant instead. Compact mode reduces padding to match filter inputs (default: false) */
  compact?: boolean;
  /** Input styling variant: 'default' for standard forms, 'compact' for inline filters, 'modal' for modal forms */
  inputVariant?: DateTimePickerInputVariant;
  /** Automatically flip dropdown direction if there's not enough space (default: false) */
  autoFlip?: boolean;
  /** Accessible name for the text input (when there's no visible label, e.g.
   *  labelType="none"). Improves a11y and lets tests target the field. */
  ariaLabel?: string;
  /** Extra classes for the input box itself (not the outer wrapper) — use to
   *  tint the field, e.g. a highlight fill. `!`-prefix to win over the base
   *  bg/border (e.g. "!bg-purple-50 !border-purple-400"). */
  inputClassName?: string;
  /** Show the clear (x) button when a value is set. Off for fields that must
   *  always hold a date -- a control that empties into an invalid state is
   *  worse than no control. Default true, so existing callers are unchanged. */
  allowClear?: boolean;
  /** Optional range shortcuts supplied by the owning report; selecting one closes the calendar. */
  calendarShortcuts?: { label: string; onSelect: () => void }[];
  /** Optional per-day gate, applied on top of minDate/maxDate. Return false and
   *  the day is greyed out, unclickable, and refused if typed into the box.
   *  For calendars where only certain days mean anything — a payroll week
   *  ending, say, where only Sundays exist. Keep it cheap: it runs per rendered
   *  day cell. */
  isDateSelectable?: (date: Date) => boolean;
}

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Generate highlight color styles based on theme
function getHighlightStyles(themeEnabled: boolean, hue: number, saturation: number) {
  if (!themeEnabled) {
    // Default slate colors when theme is disabled
    return {
      selectedBg: 'bg-slate-700 dark:bg-slate-800',
      selectedText: 'text-white',
      todayText: 'text-slate-700 dark:text-slate-400',
      rangeBg: 'bg-slate-50 dark:bg-slate-800/50',
      hoverBg: 'hover:bg-gray-100',
      focusRing: 'focus:ring-slate-600',
      buttonBg: 'bg-slate-700 hover:bg-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700',
      buttonFocusRing: 'focus-visible:outline-slate-600',
    };
  }

  // Use theme color - create inline styles
  const sat = Math.max(saturation * 0.5, 50); // Ensure visible saturation
  return {
    selectedBgStyle: `hsl(${hue}, ${sat}%, 45%)`,
    selectedText: 'text-white',
    todayTextStyle: `hsl(${hue}, ${sat}%, 45%)`,
    rangeBgStyle: `hsla(${hue}, ${sat}%, 50%, 0.1)`,
    hoverBg: 'hover:bg-gray-100',
    focusRingStyle: `hsl(${hue}, ${sat}%, 45%)`,
    buttonBgStyle: `hsl(${hue}, ${sat}%, 45%)`,
    buttonHoverBgStyle: `hsl(${hue}, ${sat}%, 40%)`,
  };
}

/**
 * Parse a typed clock time as a 24-hour value. Bare numeric entry is treated
 * as a 24-hour clock (e.g. "1500" -> 15:00, "900" -> 9:00, "9" -> 9:00,
 * "12:00"/"1200" -> noon). An explicit am/pm suffix is honored; without one,
 * no meridiem is assumed. Returns null for anything unparseable or out of range.
 *
 * Accepted forms (optionally followed by am/pm, with or without a space):
 *   "H", "HH"        -> hour only, minute 0   (9 -> 9:00, 15 -> 15:00)
 *   "HMM", "HHMM"    -> last two digits are minutes   (930 -> 9:30, 1500 -> 15:00)
 *   "H:MM", "HH:MM"  -> explicit hour and minute
 */
export function parseClockTime(raw: string): { hour: number; minute: number } | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;

  // Strip an optional trailing am/pm token (with/without space, optional dot).
  let meridiem: 'am' | 'pm' | null = null;
  let body = s;
  const mer = s.match(/(am|pm)\.?$/);
  if (mer && mer.index !== undefined) {
    meridiem = mer[1] as 'am' | 'pm';
    body = s.slice(0, mer.index).trim();
  }

  let hour: number;
  let minute: number;

  if (body.includes(':')) {
    const [h, m] = body.split(':');
    hour = parseInt(h, 10);
    minute = parseInt(m, 10);
  } else if (/^\d{1,4}$/.test(body)) {
    if (body.length <= 2) {
      hour = parseInt(body, 10);
      minute = 0;
    } else {
      // 3-4 digits: the last two are minutes, the rest is the hour.
      const cut = body.length - 2;
      hour = parseInt(body.slice(0, cut), 10);
      minute = parseInt(body.slice(cut), 10);
    }
  } else {
    return null;
  }

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  // An explicit am/pm applies 12-hour semantics; without one the value is 24-hour.
  if (meridiem === 'pm' && hour < 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/**
 * How a committed single value is rendered in the text box.
 *
 * Shared by the value→display resync effect and by the blur handler's revert, so
 * a rejected keystroke always falls back to exactly what the resync would show.
 * Returns null when `value` carries no usable date, meaning "leave the box alone".
 */
function formatSingleValueForInput(
  value: Date | DateTimeValue | null | undefined,
  mode: DateTimePickerMode
): string | null {
  const asDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const asTime = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const qualifierPrefix = (q?: TimeQualifier) =>
    !q || q === 'at' ? '' : q.charAt(0).toUpperCase() + q.slice(1) + ' ';

  if (value && typeof value === 'object' && 'date' in value && value.date) {
    const d = value.date;
    if (mode === 'date') return asDate(d);
    if (mode === 'time') return `${qualifierPrefix(value.timeQualifier)}${asTime(d)}`;
    return `${qualifierPrefix(value.timeQualifier)}${asDate(d)} ${asTime(d)}`;
  }

  if (value instanceof Date) {
    if (mode === 'date') return asDate(value);
    if (mode === 'time') return asTime(value);
    return `${asDate(value)} ${asTime(value)}`;
  }

  if (!value) return '';
  return null;
}

/** Keep a portalled dropdown inside the viewport horizontally.
 *
 *  autoFlip only ever flipped up or down; left was taken straight from the
 *  input, so a picker near the right edge rendered a calendar that ran off
 *  the screen and was clipped. Measures the dropdown when it is already open
 *  and falls back to an estimate on the first frame.
 */
export function clampLeft(left: number, dropdown: HTMLElement | null): number {
  const MARGIN = 8;
  const width = dropdown?.offsetWidth || 340;
  const maxLeft = window.scrollX + window.innerWidth - width - MARGIN;
  return Math.max(window.scrollX + MARGIN, Math.min(left, maxLeft));
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  mode = 'datetime',
  selectionType = 'single',
  value = null,
  onChange,
  placeholder,
  disabled: disabledProp = false,
  minDate,
  maxDate,
  isDateSelectable,
  minuteStep = 5,
  use24Hour = false,
  className = '',
  labelType = 'auto',
  label,
  error,
  required = false,
  showTimeQualifier = false,
  autoApply = false,
  openDirection = 'down',
  usePortal: usePortalProp = false,
  portalContainer,
  compact = false,
  inputVariant,
  autoFlip = false,
  ariaLabel,
  inputClassName = '',
  allowClear = true,
  calendarShortcuts = [],
}) => {
  const disabledByPermission = useIsDisabledByPermission();
  const disabled = disabledProp || disabledByPermission;
  // Field display prefs: `plain` flattens the shell and drops the picker
  // affordances (a locked date is a value to read, not a control to poke);
  // `shellClass` marks this element as the box for the CSS-only options.
  const { plain: plainLocked, className: shellClass } = useFieldShellDisplay(disabled);

  const usePortal = usePortalProp || !!portalContainer;

  // Resolve effective variant: inputVariant takes precedence, then compact (for backwards compat)
  const effectiveVariant: DateTimePickerInputVariant = inputVariant || (compact ? 'compact' : 'default');

  // Track the effective direction (may be flipped if autoFlip is enabled)
  const [effectiveDirection, setEffectiveDirection] = useState<DateTimePickerOpenDirection>(openDirection);

  // Input styling based on variant
  const getInputStyles = () => {
    switch (effectiveVariant) {
      case 'compact':
        return {
          container: 'bg-white py-1 ps-2 pe-2 text-xs border border-gray-400 rounded-lg focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:focus-within:ring-gray-600 leading-none',
          button: 'bg-white py-1 ps-2 pe-2 text-xs border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:focus:ring-gray-600',
          text: 'text-xs',
          gap: 'gap-x-1',
          iconGap: 'gap-0.5',
          iconSize: 'h-4 w-4',
          iconPadding: 'p-0',
          // Compact uses min-widths to ensure content fits
          minWidthSingle: 'min-w-[100px]',
          minWidthDatetimeSingle: 'min-w-[180px]',
          minWidthRange: 'min-w-[130px]',
          minWidthDatetimeRange: 'min-w-[280px]',
        };
      case 'modal':
        return {
          container: 'bg-panel px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent transition-all',
          button: 'bg-panel px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all',
          text: 'text-sm',
          gap: 'gap-x-2',
          iconGap: 'gap-1',
          iconSize: 'h-5 w-5',
          iconPadding: 'p-0.5',
          // Modal variant has no min-widths - adapts to container width
          minWidthSingle: '',
          minWidthDatetimeSingle: '',
          minWidthRange: '',
          minWidthDatetimeRange: '',
        };
      default: // 'default'
        return {
          container: 'bg-panel px-3 py-2 text-sm rounded-md shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus-within:ring-2',
          button: 'bg-panel px-3 py-2 text-sm rounded-md shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2',
          text: 'text-sm',
          gap: 'gap-x-1.5',
          iconGap: 'gap-1',
          iconSize: 'h-5 w-5',
          iconPadding: 'p-0.5',
          // Default uses min-widths
          minWidthSingle: 'min-w-[100px]',
          minWidthDatetimeSingle: 'min-w-[180px]',
          minWidthRange: 'min-w-[130px]',
          minWidthDatetimeRange: 'min-w-[280px]',
        };
    }
  };

  const inputStyles = getInputStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedHour, setSelectedHour] = useState<number>(12);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);
  const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>('AM');
  // Time qualifier state (for Before/After)
  const [selectedTimeQualifier, setSelectedTimeQualifier] = useState<TimeQualifier>('at');
  // End time state for time range mode
  const [selectedEndHour, setSelectedEndHour] = useState<number>(12);
  const [selectedEndMinute, setSelectedEndMinute] = useState<number>(0);
  const [selectedEndPeriod, setSelectedEndPeriod] = useState<'AM' | 'PM'>('PM');
  const [selectedEndTimeQualifier, setSelectedEndTimeQualifier] = useState<TimeQualifier>('at');
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  // Pending display value for range modes (set immediately on confirm, before parent updates)
  const [pendingDisplayValue, setPendingDisplayValue] = useState<string | null>(null);
  // Typed text for the two boxes in time-range mode (mode='time' + range)
  const [startTimeText, setStartTimeText] = useState<string>('');
  const [endTimeText, setEndTimeText] = useState<string>('');

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // True while a text input is focused. The value->text sync effects skip while
  // focused so a parent re-render (common with an unstable `value` prop) can't
  // clobber the user's in-progress edit.
  const isFieldFocusedRef = useRef(false);
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);
  const periodScrollRef = useRef<HTMLDivElement>(null);
  // End time refs for time range mode
  const endHourScrollRef = useRef<HTMLDivElement>(null);
  const endMinuteScrollRef = useRef<HTMLDivElement>(null);
  const endPeriodScrollRef = useRef<HTMLDivElement>(null);

  // Get theme context (useTheme returns safe defaults if context is unavailable)
  const { themeEnabled, lightThemeColor } = useTheme();
  const highlightStyles = getHighlightStyles(themeEnabled, lightThemeColor?.hue ?? 220, lightThemeColor?.saturation ?? 0);

  // Initialize from value prop
  useEffect(() => {
    if (value) {
      if (value instanceof Date) {
        setSelectedDate(value);
        setCurrentMonth(new Date(value.getFullYear(), value.getMonth(), 1));
        const hours = value.getHours();
        const minutes = value.getMinutes();
        if (use24Hour) {
          setSelectedHour(hours);
        } else {
          setSelectedHour(hours === 0 ? 12 : hours > 12 ? hours - 12 : hours);
          setSelectedPeriod(hours >= 12 ? 'PM' : 'AM');
        }
        setSelectedMinute(minutes);
      } else if ('date' in value && value.date) {
        setSelectedDate(value.date);
        setCurrentMonth(new Date(value.date.getFullYear(), value.date.getMonth(), 1));
      } else if ('startDate' in value && value.startDate) {
        setRangeStart(value.startDate);
        setRangeEnd(value.endDate || null);
        setCurrentMonth(new Date(value.startDate.getFullYear(), value.startDate.getMonth(), 1));
      }
    }
  }, [value, use24Hour]);

  // Clear pending display value when actual value prop updates
  useEffect(() => {
    setPendingDisplayValue(null);
  }, [value]);

  // Sync inputValue with the formatted display value when value changes
  useEffect(() => {
    // Don't overwrite what the user is actively typing.
    if (isFieldFocusedRef.current) return;
    if (selectionType !== 'single') return;
    const display = formatSingleValueForInput(value, mode);
    if (display !== null) setInputValue(display);
  }, [value, mode, selectionType, use24Hour]);

  // Put the box back to the committed value after input we won't accept, so a
  // rejected entry never masquerades as the current filter.
  const revertInputToValue = () => {
    setInputValue(formatSingleValueForInput(value, mode) ?? '');
  };

  /**
   * Is the COMMITTED value (the `value` prop) already empty?
   *
   * Blurring an empty box used to emit onChange(null) unconditionally, so merely
   * tabbing through a blank date/time cell reported a change that had not happened.
   * Consumers that treat any onChange as an edit then staged a blank — harmless while
   * the field really was blank, and destructive the moment the underlying data moved
   * (PRO 5140356: a driver's origin arrival/departure were overwritten by exactly such
   * a staged blank). It also marked clean forms dirty, which raises the navigate-away
   * "unsaved changes" guard for nothing.
   *
   * Reuses formatSingleValueForInput so "empty" means the same thing here as it does
   * for the input display and revertInputToValue.
   */
  const committedValueIsEmpty = () =>
    !(formatSingleValueForInput(value, mode) ?? '').trim();

  // Sync the two typed boxes for time-range mode from the value prop
  useEffect(() => {
    if (mode !== 'time' || selectionType !== 'range') return;
    // Don't overwrite what the user is actively typing.
    if (isFieldFocusedRef.current) return;
    const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    if (value && typeof value === 'object' && 'startDate' in value) {
      setStartTimeText(value.startDate ? fmt(value.startDate) : '');
      setEndTimeText(value.endDate ? fmt(value.endDate) : '');
    } else if (!value) {
      setStartTimeText('');
      setEndTimeText('');
    }
  }, [value, mode, selectionType]);

  // Calculate dropdown position for portal mode and handle auto-flip
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      // Estimate dropdown height (calendar + time picker + footer)
      const estimatedDropdownHeight = (mode === 'datetime' ? 420 : mode === 'time' ? 300 : 380)
        + (mode !== 'time' && calendarShortcuts.length ? Math.ceil(calendarShortcuts.length / 2) * 32 + 12 : 0);

      // Check if there's enough space below
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Determine effective direction
      let newDirection = openDirection;
      if (autoFlip) {
        const isDownward = openDirection === 'down' || openDirection === 'downleft' || openDirection === 'downright';
        const isUpward = openDirection === 'up' || openDirection === 'upleft' || openDirection === 'upright';

        if (isDownward && spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow) {
          // Flip to upward
          if (openDirection === 'down') newDirection = 'up';
          else if (openDirection === 'downleft') newDirection = 'upleft';
          else if (openDirection === 'downright') newDirection = 'upright';
        } else if (isUpward && spaceAbove < estimatedDropdownHeight && spaceBelow > spaceAbove) {
          // Flip to downward
          if (openDirection === 'up') newDirection = 'down';
          else if (openDirection === 'upleft') newDirection = 'downleft';
          else if (openDirection === 'upright') newDirection = 'downright';
        }
      }

      setEffectiveDirection(newDirection);

      // Calculate position for portal mode
      if (usePortal) {
        const isUpward = newDirection === 'up' || newDirection === 'upleft' || newDirection === 'upright';

        if (isUpward) {
          // Position above the input - we need to calculate this after the dropdown renders
          // For now, estimate and position above
          setDropdownPosition({
            top: rect.top + window.scrollY - estimatedDropdownHeight - 8, // 8px gap
            left: clampLeft(rect.left + window.scrollX, dropdownRef.current)
          });
        } else {
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 8, // 8px gap (mt-2)
            left: clampLeft(rect.left + window.scrollX, dropdownRef.current)
          });
        }
      }
    }
  }, [isOpen, usePortal, autoFlip, openDirection, mode, calendarShortcuts.length]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);

      // Close if click is outside both the container AND the dropdown
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Reposition dropdown on scroll/resize (prevents disconnected dropdown in scrollable containers)
  useEffect(() => {
    if (!isOpen || !usePortal || !containerRef.current) return;

    const updatePosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const estimatedDropdownHeight = (mode === 'datetime' ? 420 : mode === 'time' ? 300 : 380)
        + (mode !== 'time' && calendarShortcuts.length ? Math.ceil(calendarShortcuts.length / 2) * 32 + 12 : 0);
      const isUpward = effectiveDirection === 'up' || effectiveDirection === 'upleft' || effectiveDirection === 'upright';

      setDropdownPosition({
        top: isUpward
          ? rect.top + window.scrollY - estimatedDropdownHeight - 8
          : rect.bottom + window.scrollY + 8,
        // Scroll and resize reposition too, so the clamp belongs here as well:
        // narrowing the window must pull the calendar back into view.
        left: clampLeft(rect.left + window.scrollX, dropdownRef.current),
      });
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, usePortal, effectiveDirection, mode]);

  // Scroll time columns to selected values when opening
  useEffect(() => {
    if (isOpen && (mode === 'time' || mode === 'datetime')) {
      setTimeout(() => {
        scrollToSelected(hourScrollRef, selectedHour, use24Hour ? 24 : 12);
        scrollToSelected(minuteScrollRef, selectedMinute / minuteStep, 60 / minuteStep);
        if (!use24Hour && periodScrollRef.current) {
          const periodIndex = selectedPeriod === 'AM' ? 0 : 1;
          scrollToSelected(periodScrollRef, periodIndex, 2);
        }
        // Also scroll end time columns for time/datetime range mode
        if (selectionType === 'range' && (mode === 'time' || mode === 'datetime')) {
          scrollToSelected(endHourScrollRef, selectedEndHour, use24Hour ? 24 : 12);
          scrollToSelected(endMinuteScrollRef, selectedEndMinute / minuteStep, 60 / minuteStep);
          if (!use24Hour && endPeriodScrollRef.current) {
            const endPeriodIndex = selectedEndPeriod === 'AM' ? 0 : 1;
            scrollToSelected(endPeriodScrollRef, endPeriodIndex, 2);
          }
        }
      }, 50);
    }
  }, [isOpen, mode, selectionType, selectedHour, selectedMinute, selectedPeriod, selectedEndHour, selectedEndMinute, selectedEndPeriod, use24Hour, minuteStep]);

  const scrollToSelected = (ref: React.RefObject<HTMLDivElement | null>, index: number, _total: number) => {
    if (ref.current) {
      const itemHeight = 32;
      const containerHeight = ref.current.clientHeight;
      const scrollTop = (index * itemHeight) - (containerHeight / 2) + (itemHeight / 2);
      ref.current.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    }
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Array<{ date: Date; isCurrentMonth: boolean; isToday: boolean }> = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.getTime() === today.getTime(),
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.getTime() === today.getTime(),
      });
    }

    return days;
  }, [currentMonth]);

  // Generate hours
  const hours = useMemo(() => {
    if (use24Hour) {
      return Array.from({ length: 24 }, (_, i) => i);
    }
    return Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i);
  }, [use24Hour]);

  // Generate minutes
  const minutes = useMemo(() => {
    return Array.from({ length: 60 / minuteStep }, (_, i) => i * minuteStep);
  }, [minuteStep]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const isDateDisabled = (date: Date): boolean => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    if (isDateSelectable && !isDateSelectable(date)) return true;
    return false;
  };

  const isDateSelected = (date: Date): boolean => {
    if (selectionType === 'single' && selectedDate) {
      return date.toDateString() === selectedDate.toDateString();
    }
    if (selectionType === 'range') {
      if (rangeStart && date.toDateString() === rangeStart.toDateString()) return true;
      if (rangeEnd && date.toDateString() === rangeEnd.toDateString()) return true;
    }
    return false;
  };

  const isDateInRange = (date: Date): boolean => {
    if (selectionType !== 'range') return false;

    const start = rangeStart;
    const end = rangeEnd || hoverDate;

    if (!start || !end) return false;

    const dateTime = date.getTime();
    const startTime = Math.min(start.getTime(), end.getTime());
    const endTime = Math.max(start.getTime(), end.getTime());

    return dateTime > startTime && dateTime < endTime;
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    if (selectionType === 'single') {
      setSelectedDate(date);
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));

      // Auto-apply for single date-only picker
      if (autoApply && mode === 'date') {
        // Update input display immediately
        setInputValue(date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }));
        onChange?.(date);
        setIsOpen(false);
      }
    } else {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(date);
        setRangeEnd(null);
      } else {
        if (date < rangeStart) {
          setRangeEnd(rangeStart);
          setRangeStart(date);
        } else {
          setRangeEnd(date);
        }
      }
    }
  };

  const handleTimeChange = (type: 'hour' | 'minute' | 'period', value: number | string) => {
    if (type === 'hour') {
      setSelectedHour(value as number);
    } else if (type === 'minute') {
      setSelectedMinute(value as number);
    } else if (type === 'period') {
      setSelectedPeriod(value as 'AM' | 'PM');
    }
  };

  const handleEndTimeChange = (type: 'hour' | 'minute' | 'period', value: number | string) => {
    if (type === 'hour') {
      setSelectedEndHour(value as number);
    } else if (type === 'minute') {
      setSelectedEndMinute(value as number);
    } else if (type === 'period') {
      setSelectedEndPeriod(value as 'AM' | 'PM');
    }
  };

  const handleConfirm = () => {
    let result: Date | DateTimeValue | null = null;

    // Helper to apply time to a date
    const applyTimeToDate = (date: Date | null): Date | undefined => {
      if (!date) return undefined;
      const newDate = new Date(date);
      let hours = selectedHour;
      if (!use24Hour) {
        if (selectedPeriod === 'PM' && selectedHour !== 12) hours += 12;
        if (selectedPeriod === 'AM' && selectedHour === 12) hours = 0;
      }
      newDate.setHours(hours, selectedMinute, 0, 0);
      return newDate;
    };

    if (mode === 'date') {
      if (selectionType === 'single') {
        result = selectedDate;
      } else {
        result = { startDate: rangeStart || undefined, endDate: rangeEnd || undefined };
      }
    } else if (mode === 'time') {
      if (selectionType === 'single') {
        const date = new Date();
        let hours = selectedHour;
        if (!use24Hour) {
          if (selectedPeriod === 'PM' && selectedHour !== 12) hours += 12;
          if (selectedPeriod === 'AM' && selectedHour === 12) hours = 0;
        }
        date.setHours(hours, selectedMinute, 0, 0);
        // If showTimeQualifier is enabled, return DateTimeValue with qualifier
        if (showTimeQualifier) {
          result = { date, timeQualifier: selectedTimeQualifier };
        } else {
          result = date;
        }
      } else {
        // Time range mode - create two dates with start and end times
        const startDate = new Date();
        let startHours = selectedHour;
        if (!use24Hour) {
          if (selectedPeriod === 'PM' && selectedHour !== 12) startHours += 12;
          if (selectedPeriod === 'AM' && selectedHour === 12) startHours = 0;
        }
        startDate.setHours(startHours, selectedMinute, 0, 0);

        const endDate = new Date();
        let endHours = selectedEndHour;
        if (!use24Hour) {
          if (selectedEndPeriod === 'PM' && selectedEndHour !== 12) endHours += 12;
          if (selectedEndPeriod === 'AM' && selectedEndHour === 12) endHours = 0;
        }
        endDate.setHours(endHours, selectedEndMinute, 0, 0);

        result = {
          startDate,
          endDate,
          ...(showTimeQualifier && {
            startTimeQualifier: selectedTimeQualifier,
            endTimeQualifier: selectedEndTimeQualifier
          })
        };

        // Update display immediately for time range
        const formatTime = (date: Date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        setPendingDisplayValue(`${formatTime(startDate)} to ${formatTime(endDate)}`);
      }
    } else {
      // datetime mode
      if (selectionType === 'single') {
        const date = selectedDate ? new Date(selectedDate) : new Date();
        let hours = selectedHour;
        if (!use24Hour) {
          if (selectedPeriod === 'PM' && selectedHour !== 12) hours += 12;
          if (selectedPeriod === 'AM' && selectedHour === 12) hours = 0;
        }
        date.setHours(hours, selectedMinute, 0, 0);
        // If showTimeQualifier is enabled, return DateTimeValue with qualifier
        if (showTimeQualifier) {
          result = { date, timeQualifier: selectedTimeQualifier };
        } else {
          result = date;
        }
      } else {
        // Range mode with datetime - apply start time to start date, end time to end date
        const applyStartTime = (date: Date | null): Date | undefined => {
          if (!date) return undefined;
          const newDate = new Date(date);
          let hours = selectedHour;
          if (!use24Hour) {
            if (selectedPeriod === 'PM' && selectedHour !== 12) hours += 12;
            if (selectedPeriod === 'AM' && selectedHour === 12) hours = 0;
          }
          newDate.setHours(hours, selectedMinute, 0, 0);
          return newDate;
        };

        const applyEndTime = (date: Date | null): Date | undefined => {
          if (!date) return undefined;
          const newDate = new Date(date);
          let hours = selectedEndHour;
          if (!use24Hour) {
            if (selectedEndPeriod === 'PM' && selectedEndHour !== 12) hours += 12;
            if (selectedEndPeriod === 'AM' && selectedEndHour === 12) hours = 0;
          }
          newDate.setHours(hours, selectedEndMinute, 0, 0);
          return newDate;
        };

        result = {
          startDate: applyStartTime(rangeStart),
          endDate: applyEndTime(rangeEnd),
          ...(showTimeQualifier && {
            startTimeQualifier: selectedTimeQualifier,
            endTimeQualifier: selectedEndTimeQualifier
          })
        };
      }
    }

    onChange?.(result);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDate(null);
    setRangeStart(null);
    setRangeEnd(null);
    setInputValue('');
    setPendingDisplayValue(null);
    onChange?.(null);
  };

  // Parse typed date input (supports MM/DD/YYYY, MM/DD/YY, MM-DD-YYYY, day offsets, and 'n' for now)
  // Day offsets: 0 = today, 1 = tomorrow, -1 = yesterday, 45 = 45 days from now, etc.
  // 'n' or 'now' = current date/time
  const parseTypedDate = (input: string): Date | null => {
    if (!input.trim()) return null;

    const trimmedInput = input.trim().toLowerCase();

    // Check for 'n' or 'now' shortcut - returns current date/time
    if (trimmedInput === 'n' || trimmedInput === 'now') {
      return new Date();
    }

    // Check if input is a numeric day offset (including negative numbers)
    // Matches: "0", "-1", "1", "45", "-32", etc. (max 3 digits: -999 to 999)
    // 4+ digits are treated as MMDD format instead
    if (/^-?\d{1,3}$/.test(trimmedInput)) {
      const dayOffset = parseInt(trimmedInput, 10);
      if (dayOffset >= -999 && dayOffset <= 999) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        today.setDate(today.getDate() + dayOffset);
        return today;
      }
    }

    // Check if input is 4 digits (MMDD format) - use current year with Dec/Jan boundary logic
    if (/^\d{4}$/.test(trimmedInput)) {
      const month = parseInt(trimmedInput.substring(0, 2), 10);
      const day = parseInt(trimmedInput.substring(2, 4), 10);
      const now = new Date();
      const currentMonth = now.getMonth(); // 0-11 (0=Jan, 11=Dec)
      let year = now.getFullYear();

      // Validate month/day before proceeding
      if (month < 1 || month > 12 || day < 1 || day > 31) {
        // Invalid, fall through to other parsing
      } else {
        // Handle December/January boundary (matching legacy CMS logic)
        // If we're in January (0) and entering November (11) or December (12), use previous year
        if (currentMonth === 0 && (month === 11 || month === 12)) {
          year = year - 1;
        }
        // If we're in November (10) or December (11) and entering January (1), use next year
        else if ((currentMonth === 10 || currentMonth === 11) && month === 1) {
          year = year + 1;
        }

        const date = new Date(year, month - 1, day);
        if (date.getMonth() === month - 1 && date.getDate() === day) {
          return date;
        }
      }
    }

    // Check if input is 8 digits (MMDDYYYY format)
    if (/^\d{8}$/.test(trimmedInput)) {
      const month = parseInt(trimmedInput.substring(0, 2), 10);
      const day = parseInt(trimmedInput.substring(2, 4), 10);
      const year = parseInt(trimmedInput.substring(4, 8), 10);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
        const date = new Date(year, month - 1, day);
        if (date.getMonth() === month - 1 && date.getDate() === day) {
          return date;
        }
      }
    }

    // Try various date formats
    const cleanInput = trimmedInput.replace(/-/g, '/');
    const parts = cleanInput.split('/');

    if (parts.length >= 2) {
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      let year = parts[2] ? parseInt(parts[2], 10) : new Date().getFullYear();

      // Handle 2-digit year
      if (year < 100) {
        year = year + 2000;
      }

      // Validate date parts
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
        const date = new Date(year, month - 1, day);
        // Verify the date is valid (handles cases like Feb 31)
        if (date.getMonth() === month - 1 && date.getDate() === day) {
          return date;
        }
      }
    }

    return null;
  };

  // Parse typed datetime input - supports all date formats plus optional time
  // If no time provided, uses current time
  const parseTypedDateTime = (input: string): Date | null => {
    if (!input.trim()) return null;

    const trimmedInput = input.trim();

    // Check for 'n' or 'now' shortcut
    if (trimmedInput.toLowerCase() === 'n' || trimmedInput.toLowerCase() === 'now') {
      return new Date();
    }

    // Split input into date and time parts
    const parts = trimmedInput.split(/\s+/);
    const datePart = parts[0];
    const timePart = parts[1];
    const ampmPart = parts[2]?.toUpperCase();

    // Parse the date portion using the same logic as parseTypedDate
    let parsedDate: Date | null = null;
    const now = new Date();

    // Check for day offset (1-3 digits)
    if (/^-?\d{1,3}$/.test(datePart)) {
      const dayOffset = parseInt(datePart, 10);
      if (dayOffset >= -999 && dayOffset <= 999) {
        parsedDate = new Date();
        parsedDate.setDate(parsedDate.getDate() + dayOffset);
      }
    }
    // Check for MMDD (4 digits)
    else if (/^\d{4}$/.test(datePart)) {
      const month = parseInt(datePart.substring(0, 2), 10);
      const day = parseInt(datePart.substring(2, 4), 10);
      const currentMonth = now.getMonth();
      let year = now.getFullYear();

      // Dec/Jan boundary logic
      if (currentMonth === 0 && (month === 11 || month === 12)) {
        year = year - 1;
      } else if ((currentMonth === 10 || currentMonth === 11) && month === 1) {
        year = year + 1;
      }

      const date = new Date(year, month - 1, day);
      if (date.getMonth() === month - 1 && date.getDate() === day) {
        parsedDate = date;
      }
    }
    // Check for MMDDYY (6 digits)
    else if (/^\d{6}$/.test(datePart)) {
      const month = parseInt(datePart.substring(0, 2), 10);
      const day = parseInt(datePart.substring(2, 4), 10);
      const year = 2000 + parseInt(datePart.substring(4, 6), 10);
      const date = new Date(year, month - 1, day);
      if (date.getMonth() === month - 1 && date.getDate() === day) {
        parsedDate = date;
      }
    }
    // Check for MMDDYYYY (8 digits)
    else if (/^\d{8}$/.test(datePart)) {
      const month = parseInt(datePart.substring(0, 2), 10);
      const day = parseInt(datePart.substring(2, 4), 10);
      const year = parseInt(datePart.substring(4, 8), 10);
      const date = new Date(year, month - 1, day);
      if (date.getMonth() === month - 1 && date.getDate() === day) {
        parsedDate = date;
      }
    }
    // Check for delimited format (MM/DD, MM/DD/YY, MM/DD/YYYY)
    else if (datePart.includes('/') || datePart.includes('-')) {
      const cleanDate = datePart.replace(/-/g, '/');
      const dateParts = cleanDate.split('/');
      if (dateParts.length >= 2) {
        const month = parseInt(dateParts[0], 10);
        const day = parseInt(dateParts[1], 10);
        let year = dateParts[2] ? parseInt(dateParts[2], 10) : now.getFullYear();
        if (year < 100) year += 2000;

        // Apply Dec/Jan boundary logic when no year provided
        if (!dateParts[2]) {
          const currentMonth = now.getMonth();
          if (currentMonth === 0 && (month === 11 || month === 12)) {
            year = year - 1;
          } else if ((currentMonth === 10 || currentMonth === 11) && month === 1) {
            year = year + 1;
          }
        }

        const date = new Date(year, month - 1, day);
        if (date.getMonth() === month - 1 && date.getDate() === day) {
          parsedDate = date;
        }
      }
    }

    if (!parsedDate) return null;

    // Parse time if provided, otherwise use current time
    if (timePart) {
      // Same 24-hour clock parser the time-only field uses, so a colon-less
      // entry ("16", "930") is read as a time instead of being dropped on the
      // floor and leaving the value at midnight. A meridiem typed as its own
      // token ("4 pm") is re-joined for parseClockTime, which strips it itself.
      const clock = parseClockTime(ampmPart ? `${timePart} ${ampmPart}` : timePart);
      if (clock) {
        parsedDate.setHours(clock.hour, clock.minute, 0, 0);
      }
    } else {
      // No time provided - default to midnight (00:00). Day-offset shortcuts
      // like "0"/"-1" and bare typed dates come in at 00:00; only the "n"/"now"
      // shortcut (handled above) uses the current time.
      parsedDate.setHours(0, 0, 0, 0);
    }

    return parsedDate;
  };

  // Parse typed time input. Bare/colon times are 24-hour (see parseClockTime);
  // also supports a leading "before/after/at" qualifier (or "b"/"a" shorthand)
  // and the 'n'/'now' shortcut.
  const parseTypedTime = (input: string): Date | { date: Date; timeQualifier: TimeQualifier } | null => {
    if (!input.trim()) return null;

    const trimmed = input.trim();
    const lower = trimmed.toLowerCase();

    // Check for 'n' or 'now' shortcut
    if (lower === 'n' || lower === 'now') {
      return new Date();
    }

    // Check for qualifier prefix (full words, or "b"/"a" shorthand before a number)
    let qualifier: TimeQualifier = 'at';
    let timeInput = trimmed;

    if (lower.startsWith('before ')) {
      qualifier = 'before';
      timeInput = trimmed.substring(7); // Remove "before "
    } else if (lower.startsWith('after ')) {
      qualifier = 'after';
      timeInput = trimmed.substring(6); // Remove "after "
    } else if (lower.startsWith('at ')) {
      qualifier = 'at';
      timeInput = trimmed.substring(3); // Remove "at "
    } else if (/^b\s*\d/.test(lower)) {
      // Shorthand: "b 4" or "b4" = before
      qualifier = 'before';
      timeInput = trimmed.replace(/^b\s*/i, '');
    } else if (/^a\s*\d/.test(lower)) {
      // Shorthand: "a 7" or "a7" = after
      qualifier = 'after';
      timeInput = trimmed.replace(/^a\s*/i, '');
    }

    const parsed = parseClockTime(timeInput);
    if (!parsed) return null;

    const date = new Date();
    date.setHours(parsed.hour, parsed.minute, 0, 0);

    // If showTimeQualifier is enabled or a qualifier was typed, return with qualifier
    if (showTimeQualifier || qualifier !== 'at') {
      setSelectedTimeQualifier(qualifier);
      return { date, timeQualifier: qualifier };
    }

    return date;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleInputBlur = () => {
    isFieldFocusedRef.current = false;
    if (selectionType === 'single') {
      if (mode === 'date') {
        const parsedDate = parseTypedDate(inputValue);
        // isDateDisabled, not a fresh min/max test: it also carries the
        // isDateSelectable gate, so a day the calendar refuses cannot be typed in.
        const outOfBounds = parsedDate ? isDateDisabled(parsedDate) : false;
        if (parsedDate && !outOfBounds) {
          setSelectedDate(parsedDate);
          setCurrentMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
          onChange?.(parsedDate);
          // Update input to formatted value
          setInputValue(parsedDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }));
        } else if (inputValue.trim() === '') {
          // Clear if empty — but only when there was something committed to clear.
          setSelectedDate(null);
          if (!committedValueIsEmpty()) onChange?.(null);
        } else {
          // Unparseable, or outside minDate/maxDate — show the committed value again.
          revertInputToValue();
        }
      } else if (mode === 'time') {
        const parsedTime = parseTypedTime(inputValue);
        if (parsedTime) {
          // Handle both Date and DateTimeValue
          const timeDate = parsedTime instanceof Date ? parsedTime : parsedTime.date;
          const qualifier = parsedTime instanceof Date ? undefined : parsedTime.timeQualifier;

          if (timeDate) {
            setSelectedHour(use24Hour ? timeDate.getHours() : (timeDate.getHours() % 12 || 12));
            setSelectedMinute(timeDate.getMinutes());
            setSelectedPeriod(timeDate.getHours() >= 12 ? 'PM' : 'AM');
            if (qualifier) {
              setSelectedTimeQualifier(qualifier);
            }
            onChange?.(parsedTime);
            // Update input to formatted value
            const formatQualifier = (q?: TimeQualifier) => {
              if (!q || q === 'at') return '';
              return q.charAt(0).toUpperCase() + q.slice(1) + ' ';
            };
            const timeStr = timeDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
            setInputValue(`${formatQualifier(qualifier)}${timeStr}`);
          } else {
            revertInputToValue();
          }
        } else if (inputValue.trim() === '') {
          if (!committedValueIsEmpty()) onChange?.(null);
        } else {
          revertInputToValue();
        }
      } else if (mode === 'datetime') {
        const parsedDateTime = parseTypedDateTime(inputValue);
        const outOfBounds = parsedDateTime
          ? isDateDisabled(parsedDateTime)
          : false;
        if (parsedDateTime && !outOfBounds) {
          setSelectedDate(parsedDateTime);
          setCurrentMonth(new Date(parsedDateTime.getFullYear(), parsedDateTime.getMonth(), 1));
          setSelectedHour(use24Hour ? parsedDateTime.getHours() : (parsedDateTime.getHours() % 12 || 12));
          setSelectedMinute(parsedDateTime.getMinutes());
          setSelectedPeriod(parsedDateTime.getHours() >= 12 ? 'PM' : 'AM');
          onChange?.(parsedDateTime);
          // Update input to formatted value
          setInputValue(
            parsedDateTime.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) +
            ' ' +
            parsedDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
          );
        } else if (inputValue.trim() === '') {
          setSelectedDate(null);
          if (!committedValueIsEmpty()) onChange?.(null);
        } else {
          revertInputToValue();
        }
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      inputRef.current?.blur();
    }
  };

  // --- Time-range mode (mode='time' + range): two typed boxes ---------------
  const buildTimeDate = (hm: { hour: number; minute: number }): Date => {
    const d = new Date();
    d.setHours(hm.hour, hm.minute, 0, 0);
    return d;
  };

  // Parse both boxes and emit the range value. Emits null only when both are empty/invalid.
  const commitRangeTime = () => {
    const s = parseClockTime(startTimeText);
    const e = parseClockTime(endTimeText);
    const startDate = s ? buildTimeDate(s) : undefined;
    const endDate = e ? buildTimeDate(e) : undefined;
    if (!startDate && !endDate) {
      onChange?.(null);
      return;
    }
    onChange?.({ startDate, endDate });
  };

  const handleRangeTimeBlur = () => {
    isFieldFocusedRef.current = false;
    const s = parseClockTime(startTimeText);
    const e = parseClockTime(endTimeText);
    const pad = (n: number) => String(n).padStart(2, '0');
    // Re-canonicalize each box to HH:MM once it parses (e.g. "900" -> "09:00")
    if (s) setStartTimeText(`${pad(s.hour)}:${pad(s.minute)}`);
    if (e) setEndTimeText(`${pad(e.hour)}:${pad(e.minute)}`);
    commitRangeTime();
  };

  const handleRangeTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleRangeTimeBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  const formatDisplayValue = (): string => {
    if (!value) return placeholder || '';

    // Helper to format qualifier prefix
    const formatQualifier = (qualifier?: TimeQualifier): string => {
      if (!qualifier || qualifier === 'at') return '';
      return qualifier.charAt(0).toUpperCase() + qualifier.slice(1) + ' ';
    };

    if (value instanceof Date) {
      if (mode === 'date') {
        return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      } else if (mode === 'time') {
        return value.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      } else {
        return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) +
               ' ' +
               value.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      }
    }

    if ('startDate' in value && value.startDate) {
      const formatRangeDate = (date: Date, qualifier?: TimeQualifier): string => {
        const qualifierPrefix = formatQualifier(qualifier);
        const dateStr = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
        if (mode === 'datetime') {
          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
          return `${qualifierPrefix}${dateStr} ${timeStr}`;
        } else if (mode === 'time') {
          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
          return `${qualifierPrefix}${timeStr}`;
        }
        return dateStr;
      };

      const start = formatRangeDate(value.startDate, value.startTimeQualifier);
      if (value.endDate) {
        const end = formatRangeDate(value.endDate, value.endTimeQualifier);
        return `${start} to ${end}`;
      }
      return start;
    }

    // Handle single time/datetime with qualifier
    if ('date' in value && value.date) {
      const qualifierPrefix = formatQualifier(value.timeQualifier);
      if (mode === 'time') {
        const timeStr = value.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        return `${qualifierPrefix}${timeStr}`;
      } else if (mode === 'datetime') {
        const dateStr = value.date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        const timeStr = value.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        return `${qualifierPrefix}${dateStr} ${timeStr}`;
      }
      return value.date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    }

    return placeholder || '';
  };

  const shortcutButtons = calendarShortcuts.length > 0 && (
    <div className="grid grid-cols-2 gap-1.5 mb-3" role="group" aria-label="Quick date ranges">
      {calendarShortcuts.map((shortcut) => (
        <button key={shortcut.label} type="button"
          className="rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-label hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={() => { shortcut.onSelect(); setIsOpen(false); }}>
          {shortcut.label}
        </button>
      ))}
    </div>
  );

  const showCalendar = mode === 'date' || mode === 'datetime';
  const showTime = mode === 'time' || mode === 'datetime';

  // Get the appropriate background style for selected items
  const getSelectedBgStyle = () => {
    if (themeEnabled && 'selectedBgStyle' in highlightStyles) {
      return { backgroundColor: highlightStyles.selectedBgStyle };
    }
    return {};
  };

  const getRangeBgStyle = () => {
    if (themeEnabled && 'rangeBgStyle' in highlightStyles) {
      return { backgroundColor: highlightStyles.rangeBgStyle };
    }
    return {};
  };

  const getTodayTextStyle = () => {
    if (themeEnabled && 'todayTextStyle' in highlightStyles) {
      return { color: highlightStyles.todayTextStyle };
    }
    return {};
  };

  const getButtonBgStyle = () => {
    if (themeEnabled && 'buttonBgStyle' in highlightStyles) {
      return { backgroundColor: highlightStyles.buttonBgStyle };
    }
    return {};
  };

  // Generate label based on labelType
  const getDisplayLabel = (): string | null => {
    if (labelType === 'none') return null;
    if (labelType === 'custom') return label || null;

    // Map labelType to display text
    const labelMap: Record<string, string> = {
      'date': 'Date',
      'datetime': 'Date & Time',
      'date-range': 'Date Range',
      'datetime-range': 'Date & Time Range',
      'time': 'Time',
      'time-range': 'Time Range',
    };

    if (labelType !== 'auto' && labelMap[labelType]) {
      return labelMap[labelType];
    }

    // Auto-generate based on mode and selectionType
    if (mode === 'date') {
      return selectionType === 'range' ? 'Date Range' : 'Date';
    } else if (mode === 'time') {
      return selectionType === 'range' ? 'Time Range' : 'Time';
    } else {
      return selectionType === 'range' ? 'Date & Time Range' : 'Date & Time';
    }
  };

  const displayLabel = getDisplayLabel();

  // Get position classes based on effectiveDirection (accounts for auto-flip)
  const getDropdownPositionClasses = (): string => {
    switch (effectiveDirection) {
      case 'up':
        return 'bottom-full mb-2 left-0';
      case 'upleft':
        return 'bottom-full mb-2 right-0';
      case 'upright':
        return 'bottom-full mb-2 left-0';
      case 'left':
        return 'right-full mr-2 top-0';
      case 'right':
        return 'left-full ml-2 top-0';
      case 'downleft':
        return 'top-full mt-2 right-0';
      case 'downright':
        return 'top-full mt-2 left-0';
      case 'down':
      default:
        return 'top-full mt-2 left-0';
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {displayLabel && (
        <label className="block text-sm font-medium text-label mb-1">
          {displayLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Trigger */}
      <div className="relative flex items-center">
        {selectionType === 'single' ? (
          // Text input for single modes - allows typing
          <div
            className={`
              relative flex items-center justify-between ${inputStyles.gap}
              ${inputStyles.container}
              text-gray-900 dark:text-gray-100
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${error ? 'border-red-500 ring-red-500' : ''}
              ${mode === 'datetime' ? inputStyles.minWidthDatetimeSingle : inputStyles.minWidthSingle} w-full
              ${inputClassName} ${shellClass}
            `}
            style={effectiveVariant === 'default' && themeEnabled && 'focusRingStyle' in highlightStyles ? { '--tw-ring-color': highlightStyles.focusRingStyle } as React.CSSProperties : {}}
          >
            <input
              ref={inputRef}
              type="text"
              aria-label={ariaLabel}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => { isFieldFocusedRef.current = true; }}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder || (mode === 'date' ? 'MM/DD/YYYY' : mode === 'time' ? 'HH:MM' : 'MM/DD/YYYY HH:MM AM')}
              disabled={disabled}
              className={`flex-1 bg-transparent border-none outline-none ${inputStyles.text} text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 p-0 w-full`}
            />
            <div className={`flex items-center ${inputStyles.iconGap} flex-shrink-0`}>
              {value && !disabled && allowClear && (
                <button
                  type="button"
                  onClick={handleClear}
                  className={`${inputStyles.iconPadding} rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors`}
                  title="Clear"
                >
                  <XMarkIcon className={inputStyles.iconSize} aria-hidden="true" />
                </button>
              )}
              {/* Time-only mode has no picker — type the time directly */}
              {mode !== 'time' && !plainLocked && (
                <button
                  type="button"
                  onClick={() => !disabled && setIsOpen(!isOpen)}
                  disabled={disabled}
                  className={`${inputStyles.iconPadding} rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors`}
                  title="Open calendar"
                >
                  <CalendarIcon className={inputStyles.iconSize} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        ) : mode === 'time' ? (
          // Time-range mode: two typed boxes (Start / End), no picker
          <div className={`flex items-center ${inputStyles.gap} w-full`}>
            <div
              className={`
                relative flex items-center ${inputStyles.container}
                text-gray-900 dark:text-gray-100
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${error ? 'border-red-500 ring-red-500' : ''}
                flex-1 ${inputClassName} ${shellClass}
              `}
            >
              <input
                type="text"
                aria-label={ariaLabel ? `${ariaLabel} start time` : 'Start time'}
                value={startTimeText}
                onChange={(e) => setStartTimeText(e.target.value)}
                onFocus={() => { isFieldFocusedRef.current = true; }}
                onBlur={handleRangeTimeBlur}
                onKeyDown={handleRangeTimeKeyDown}
                placeholder="HH:MM"
                disabled={disabled}
                className={`flex-1 bg-transparent border-none outline-none ${inputStyles.text} text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 p-0 w-full`}
              />
            </div>
            <span className={`${inputStyles.text} text-gray-500 dark:text-gray-400 flex-shrink-0`}>to</span>
            <div
              className={`
                relative flex items-center ${inputStyles.container}
                text-gray-900 dark:text-gray-100
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${error ? 'border-red-500 ring-red-500' : ''}
                flex-1 ${inputClassName} ${shellClass}
              `}
            >
              <input
                type="text"
                aria-label={ariaLabel ? `${ariaLabel} end time` : 'End time'}
                value={endTimeText}
                onChange={(e) => setEndTimeText(e.target.value)}
                onFocus={() => { isFieldFocusedRef.current = true; }}
                onBlur={handleRangeTimeBlur}
                onKeyDown={handleRangeTimeKeyDown}
                placeholder="HH:MM"
                disabled={disabled}
                className={`flex-1 bg-transparent border-none outline-none ${inputStyles.text} text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 p-0 w-full`}
              />
            </div>
          </div>
        ) : (
          // Button for range modes
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`
              relative flex items-center justify-between ${inputStyles.gap}
              ${inputStyles.button}
              text-left text-gray-900 dark:text-gray-100
              hover:bg-gray-50 dark:hover:bg-gray-700
              disabled:opacity-50 disabled:cursor-not-allowed
              ${error ? 'border-red-500 ring-red-500' : ''}
              ${selectionType === 'range' && mode === 'datetime' ? inputStyles.minWidthDatetimeRange : selectionType === 'range' ? inputStyles.minWidthRange : inputStyles.minWidthSingle} w-full
              ${inputClassName} ${shellClass}
            `}
            style={effectiveVariant === 'default' && themeEnabled && 'focusRingStyle' in highlightStyles ? { '--tw-ring-color': highlightStyles.focusRingStyle } as React.CSSProperties : {}}
          >
            <span className={(value || pendingDisplayValue) ? 'text-body' : 'text-gray-400 dark:text-gray-500'}>
              {pendingDisplayValue || formatDisplayValue() || placeholder || 'Select...'}
            </span>
            {!plainLocked && (
            <div className={`flex items-center ${inputStyles.iconGap}`}>
              {(value || pendingDisplayValue) && !disabled && allowClear && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e as any); }}
                  className={`${inputStyles.iconPadding} rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer`}
                  title="Clear"
                >
                  <XMarkIcon className={inputStyles.iconSize} aria-hidden="true" />
                </span>
              )}
              <CalendarIcon className={`${inputStyles.iconSize} text-gray-400 dark:text-gray-500`} aria-hidden="true" />
            </div>
            )}
          </button>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown Picker */}
      {isOpen && (usePortal ? (
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] bg-panel rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 overflow-hidden"
            style={dropdownPosition ? { top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` } : {}}
          >
          <div className="flex">
            {/* Calendar Section */}
            {showCalendar && (
              <div className="p-4 w-[280px]">
                {shortcutButtons}
                {/* Month/Year Header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={() => navigateMonth('prev')}
                    className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                  >
                    <span className="sr-only">Previous month</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>

                  <div className="flex-auto text-center">
                    <span className="text-sm font-semibold text-body">
                      {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigateMonth('next')}
                    className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                  >
                    <span className="sr-only">Next month</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                {/* Days of Week */}
                <div className="grid grid-cols-7 text-center text-xs leading-6 text-muted">
                  {DAYS_OF_WEEK.map((day, index) => (
                    <div key={index}>{day}</div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 dark:bg-gray-700 text-sm shadow ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
                  {calendarDays.map((day, index) => {
                    const isSelected = isDateSelected(day.date);
                    const isInRange = isDateInRange(day.date);
                    const isDisabled = isDateDisabled(day.date);

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleDateClick(day.date)}
                        onMouseEnter={() => selectionType === 'range' && rangeStart && !rangeEnd && setHoverDate(day.date)}
                        onMouseLeave={() => setHoverDate(null)}
                        disabled={isDisabled}
                        className={`
                          py-1.5 focus:z-10
                          ${!isSelected && !isInRange && day.isCurrentMonth ? 'bg-panel' : ''}
                          ${!isSelected && !isInRange && !day.isCurrentMonth ? 'bg-gray-50 dark:bg-gray-900' : ''}
                          ${(isSelected || day.isToday) ? 'font-semibold' : ''}
                          ${isSelected ? 'text-white' : ''}
                          ${isSelected && !themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}
                          ${!isSelected && day.isCurrentMonth && !day.isToday ? 'text-body' : ''}
                          ${!isSelected && !day.isCurrentMonth && !day.isToday ? 'text-gray-400 dark:text-gray-500' : ''}
                          ${day.isToday && !isSelected && !themeEnabled ? 'text-slate-700 dark:text-slate-400' : ''}
                          ${!isSelected && !isInRange && !isDisabled ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
                          ${isInRange && !isSelected && !themeEnabled ? 'bg-slate-100 dark:bg-slate-800/50' : ''}
                          ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        style={{
                          ...(isSelected && themeEnabled ? getSelectedBgStyle() : {}),
                          ...(isInRange && !isSelected && themeEnabled ? getRangeBgStyle() : {}),
                          ...(day.isToday && !isSelected && themeEnabled ? getTodayTextStyle() : {}),
                        }}
                      >
                        <time
                          dateTime={day.date.toISOString().split('T')[0]}
                          className="mx-auto flex h-7 w-7 items-center justify-center"
                        >
                          {day.date.getDate()}
                        </time>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Time Section */}
            {showTime && (
              <div className={`flex ${showCalendar ? 'border-l border-gray-200 dark:border-gray-700' : ''}`}>
                {/* Start Time (or single time) */}
                <div className="flex flex-col">
                  {selectionType === 'range' && (mode === 'time' || mode === 'datetime') && (
                    <div className="px-2 py-1.5 text-xs font-semibold text-label text-center bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      Start Time
                    </div>
                  )}
                  <div className="flex">
                    {/* Time Qualifier Column (Before/After) */}
                    {showTimeQualifier && (
                      <div className="flex flex-col">
                        <div className="px-2 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          &nbsp;
                        </div>
                        <div className="flex flex-col py-1 px-1">
                          {(['before', 'at', 'after'] as const).map((qualifier) => (
                            <button
                              key={qualifier}
                              type="button"
                              onClick={() => setSelectedTimeQualifier(qualifier)}
                              className={`
                                px-2 py-1.5 text-xs font-medium rounded
                                transition-colors duration-150
                                ${selectedTimeQualifier === qualifier
                                  ? `text-white ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                              `}
                              style={selectedTimeQualifier === qualifier ? getSelectedBgStyle() : {}}
                            >
                              {qualifier.charAt(0).toUpperCase() + qualifier.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Hours Column */}
                    <div className="flex flex-col">
                      <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                        Hour
                      </div>
                      <div
                        ref={hourScrollRef}
                        className="h-[220px] w-14 overflow-y-auto"
                      >
                        <div className="py-1">
                          {hours.map((hour) => (
                            <button
                              key={hour}
                              type="button"
                              onClick={() => handleTimeChange('hour', hour)}
                              className={`
                                w-full py-1.5 text-center text-sm
                                transition-colors duration-150
                                ${selectedHour === hour
                                  ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                  : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                              `}
                              style={selectedHour === hour ? getSelectedBgStyle() : {}}
                            >
                              {hour.toString().padStart(2, '0')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Minutes Column */}
                    <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                      <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                        Min
                      </div>
                      <div
                        ref={minuteScrollRef}
                        className="h-[220px] w-14 overflow-y-auto"
                      >
                        <div className="py-1">
                          {minutes.map((minute) => (
                            <button
                              key={minute}
                              type="button"
                              onClick={() => handleTimeChange('minute', minute)}
                              className={`
                                w-full py-1.5 text-center text-sm
                                transition-colors duration-150
                                ${selectedMinute === minute
                                  ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                  : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                              `}
                              style={selectedMinute === minute ? getSelectedBgStyle() : {}}
                            >
                              {minute.toString().padStart(2, '0')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* AM/PM Column */}
                    {!use24Hour && (
                      <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                        <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          &nbsp;
                        </div>
                        <div
                          ref={periodScrollRef}
                          className="h-[220px] w-14 overflow-y-auto"
                        >
                          <div className="py-1">
                            {(['AM', 'PM'] as const).map((period) => (
                              <button
                                key={period}
                                type="button"
                                onClick={() => handleTimeChange('period', period)}
                                className={`
                                  w-full py-1.5 text-center text-sm
                                  transition-colors duration-150
                                  ${selectedPeriod === period
                                    ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedPeriod === period ? getSelectedBgStyle() : {}}
                              >
                                {period}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* End Time - for time range or datetime range mode */}
                {selectionType === 'range' && (mode === 'time' || mode === 'datetime') && (
                  <div className="flex flex-col border-l-2 border-gray-300 dark:border-gray-600">
                    <div className="px-2 py-1.5 text-xs font-semibold text-label text-center bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      End Time
                    </div>
                    <div className="flex">
                      {/* End Time Qualifier Column (Before/After) */}
                      {showTimeQualifier && (
                        <div className="flex flex-col">
                          <div className="px-2 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                            &nbsp;
                          </div>
                          <div className="flex flex-col py-1 px-1">
                            {(['before', 'at', 'after'] as const).map((qualifier) => (
                              <button
                                key={qualifier}
                                type="button"
                                onClick={() => setSelectedEndTimeQualifier(qualifier)}
                                className={`
                                  px-2 py-1.5 text-xs font-medium rounded
                                  transition-colors duration-150
                                  ${selectedEndTimeQualifier === qualifier
                                    ? `text-white ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedEndTimeQualifier === qualifier ? getSelectedBgStyle() : {}}
                              >
                                {qualifier.charAt(0).toUpperCase() + qualifier.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* End Hours Column */}
                      <div className="flex flex-col">
                        <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          Hour
                        </div>
                        <div
                          ref={endHourScrollRef}
                          className="h-[220px] w-14 overflow-y-auto"
                        >
                          <div className="py-1">
                            {hours.map((hour) => (
                              <button
                                key={hour}
                                type="button"
                                onClick={() => handleEndTimeChange('hour', hour)}
                                className={`
                                  w-full py-1.5 text-center text-sm
                                  transition-colors duration-150
                                  ${selectedEndHour === hour
                                    ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedEndHour === hour ? getSelectedBgStyle() : {}}
                              >
                                {hour.toString().padStart(2, '0')}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* End Minutes Column */}
                      <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                        <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          Min
                        </div>
                        <div
                          ref={endMinuteScrollRef}
                          className="h-[220px] w-14 overflow-y-auto"
                        >
                          <div className="py-1">
                            {minutes.map((minute) => (
                              <button
                                key={minute}
                                type="button"
                                onClick={() => handleEndTimeChange('minute', minute)}
                                className={`
                                  w-full py-1.5 text-center text-sm
                                  transition-colors duration-150
                                  ${selectedEndMinute === minute
                                    ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedEndMinute === minute ? getSelectedBgStyle() : {}}
                              >
                                {minute.toString().padStart(2, '0')}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* End AM/PM Column */}
                      {!use24Hour && (
                        <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                          <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                            &nbsp;
                          </div>
                          <div
                            ref={endPeriodScrollRef}
                            className="h-[220px] w-14 overflow-y-auto"
                          >
                            <div className="py-1">
                              {(['AM', 'PM'] as const).map((period) => (
                                <button
                                  key={period}
                                  type="button"
                                  onClick={() => handleEndTimeChange('period', period)}
                                  className={`
                                    w-full py-1.5 text-center text-sm
                                    transition-colors duration-150
                                    ${selectedEndPeriod === period
                                      ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                      : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                  `}
                                  style={selectedEndPeriod === period ? getSelectedBgStyle() : {}}
                                >
                                  {period}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer - hidden when autoApply is enabled */}
          {!autoApply && (
            <div className="flex justify-end gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md bg-panel px-3 py-2 text-sm font-semibold text-body shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className={`rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${!themeEnabled ? 'bg-slate-700 hover:bg-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700' : ''} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                style={getButtonBgStyle()}
                onMouseEnter={(e) => {
                  if (themeEnabled && 'buttonHoverBgStyle' in highlightStyles) {
                    e.currentTarget.style.backgroundColor = highlightStyles.buttonHoverBgStyle as string;
                  }
                }}
                onMouseLeave={(e) => {
                  if (themeEnabled && 'buttonBgStyle' in highlightStyles) {
                    e.currentTarget.style.backgroundColor = highlightStyles.buttonBgStyle as string;
                  }
                }}
              >
                Apply
              </button>
            </div>
          )}
          </div>,
          // A modal dialog makes body portals inert and paints above them.
          // Keep the calendar in its nearest dialog, as TypeAhead does.
          portalContainer || containerRef.current?.closest('dialog') || document.body
        )
      ) : (
        <div className={`absolute z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 overflow-hidden ${getDropdownPositionClasses()}`}>
          <div className="flex">
            {/* Calendar Section */}
            {showCalendar && (
              <div className="p-4 w-[280px]">
                {shortcutButtons}
                {/* Month/Year Header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={() => navigateMonth('prev')}
                    className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                  >
                    <span className="sr-only">Previous month</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>

                  <div className="flex-auto text-center">
                    <span className="text-sm font-semibold text-body">
                      {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigateMonth('next')}
                    className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                  >
                    <span className="sr-only">Next month</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                {/* Days of Week */}
                <div className="grid grid-cols-7 text-center text-xs leading-6 text-muted">
                  {DAYS_OF_WEEK.map((day, index) => (
                    <div key={index}>{day}</div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 dark:bg-gray-700 text-sm shadow ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
                  {calendarDays.map((day, index) => {
                    const isSelected = isDateSelected(day.date);
                    const isInRange = isDateInRange(day.date);
                    const isDisabled = isDateDisabled(day.date);

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleDateClick(day.date)}
                        onMouseEnter={() => selectionType === 'range' && rangeStart && !rangeEnd && setHoverDate(day.date)}
                        onMouseLeave={() => setHoverDate(null)}
                        disabled={isDisabled}
                        className={`
                          py-1.5 focus:z-10
                          ${!isSelected && !isInRange && day.isCurrentMonth ? 'bg-panel' : ''}
                          ${!isSelected && !isInRange && !day.isCurrentMonth ? 'bg-gray-50 dark:bg-gray-900' : ''}
                          ${(isSelected || day.isToday) ? 'font-semibold' : ''}
                          ${isSelected ? 'text-white' : ''}
                          ${isSelected && !themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}
                          ${!isSelected && day.isCurrentMonth && !day.isToday ? 'text-body' : ''}
                          ${!isSelected && !day.isCurrentMonth && !day.isToday ? 'text-gray-400 dark:text-gray-500' : ''}
                          ${day.isToday && !isSelected && !themeEnabled ? 'text-slate-700 dark:text-slate-400' : ''}
                          ${!isSelected && !isInRange && !isDisabled ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
                          ${isInRange && !isSelected && !themeEnabled ? 'bg-slate-100 dark:bg-slate-800/50' : ''}
                          ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        style={{
                          ...(isSelected && themeEnabled ? getSelectedBgStyle() : {}),
                          ...(isInRange && !isSelected && themeEnabled ? getRangeBgStyle() : {}),
                          ...(day.isToday && !isSelected && themeEnabled ? getTodayTextStyle() : {}),
                        }}
                      >
                        <time
                          dateTime={day.date.toISOString().split('T')[0]}
                          className="mx-auto flex h-7 w-7 items-center justify-center"
                        >
                          {day.date.getDate()}
                        </time>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Time Section */}
            {showTime && (
              <div className={`flex ${showCalendar ? 'border-l border-gray-200 dark:border-gray-700' : ''}`}>
                {/* Start Time (or single time) */}
                <div className="flex flex-col">
                  {selectionType === 'range' && (mode === 'time' || mode === 'datetime') && (
                    <div className="px-2 py-1.5 text-xs font-semibold text-label text-center bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      Start Time
                    </div>
                  )}
                  <div className="flex">
                    {/* Time Qualifier Column (Before/After) */}
                    {showTimeQualifier && (
                      <div className="flex flex-col">
                        <div className="px-2 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          &nbsp;
                        </div>
                        <div className="flex flex-col py-1 px-1">
                          {(['before', 'at', 'after'] as const).map((qualifier) => (
                            <button
                              key={qualifier}
                              type="button"
                              onClick={() => setSelectedTimeQualifier(qualifier)}
                              className={`
                                px-2 py-1.5 text-xs font-medium rounded
                                transition-colors duration-150
                                ${selectedTimeQualifier === qualifier
                                  ? `text-white ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                              `}
                              style={selectedTimeQualifier === qualifier ? getSelectedBgStyle() : {}}
                            >
                              {qualifier.charAt(0).toUpperCase() + qualifier.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Hours Column */}
                    <div className="flex flex-col">
                      <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                        Hour
                      </div>
                      <div
                        ref={hourScrollRef}
                        className="h-[220px] w-14 overflow-y-auto"
                      >
                        <div className="py-1">
                          {hours.map((hour) => (
                            <button
                              key={hour}
                              type="button"
                              onClick={() => handleTimeChange('hour', hour)}
                              className={`
                                w-full py-1.5 text-center text-sm
                                transition-colors duration-150
                                ${selectedHour === hour
                                  ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                  : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                              `}
                              style={selectedHour === hour ? getSelectedBgStyle() : {}}
                            >
                              {hour.toString().padStart(2, '0')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Minutes Column */}
                    <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                      <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                        Min
                      </div>
                      <div
                        ref={minuteScrollRef}
                        className="h-[220px] w-14 overflow-y-auto"
                      >
                        <div className="py-1">
                          {minutes.map((minute) => (
                            <button
                              key={minute}
                              type="button"
                              onClick={() => handleTimeChange('minute', minute)}
                              className={`
                                w-full py-1.5 text-center text-sm
                                transition-colors duration-150
                                ${selectedMinute === minute
                                  ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                  : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                              `}
                              style={selectedMinute === minute ? getSelectedBgStyle() : {}}
                            >
                              {minute.toString().padStart(2, '0')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* AM/PM Column */}
                    {!use24Hour && (
                      <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                        <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          &nbsp;
                        </div>
                        <div
                          ref={periodScrollRef}
                          className="h-[220px] w-14 overflow-y-auto"
                        >
                          <div className="py-1">
                            {(['AM', 'PM'] as const).map((period) => (
                              <button
                                key={period}
                                type="button"
                                onClick={() => handleTimeChange('period', period)}
                                className={`
                                  w-full py-1.5 text-center text-sm
                                  transition-colors duration-150
                                  ${selectedPeriod === period
                                    ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedPeriod === period ? getSelectedBgStyle() : {}}
                              >
                                {period}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* End Time - for time range or datetime range mode */}
                {selectionType === 'range' && (mode === 'time' || mode === 'datetime') && (
                  <div className="flex flex-col border-l-2 border-gray-300 dark:border-gray-600">
                    <div className="px-2 py-1.5 text-xs font-semibold text-label text-center bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      End Time
                    </div>
                    <div className="flex">
                      {/* End Time Qualifier Column (Before/After) */}
                      {showTimeQualifier && (
                        <div className="flex flex-col">
                          <div className="px-2 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                            &nbsp;
                          </div>
                          <div className="flex flex-col py-1 px-1">
                            {(['before', 'at', 'after'] as const).map((qualifier) => (
                              <button
                                key={qualifier}
                                type="button"
                                onClick={() => setSelectedEndTimeQualifier(qualifier)}
                                className={`
                                  px-2 py-1.5 text-xs font-medium rounded
                                  transition-colors duration-150
                                  ${selectedEndTimeQualifier === qualifier
                                    ? `text-white ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedEndTimeQualifier === qualifier ? getSelectedBgStyle() : {}}
                              >
                                {qualifier.charAt(0).toUpperCase() + qualifier.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* End Hours Column */}
                      <div className="flex flex-col">
                        <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          Hour
                        </div>
                        <div
                          ref={endHourScrollRef}
                          className="h-[220px] w-14 overflow-y-auto"
                        >
                          <div className="py-1">
                            {hours.map((hour) => (
                              <button
                                key={hour}
                                type="button"
                                onClick={() => handleEndTimeChange('hour', hour)}
                                className={`
                                  w-full py-1.5 text-center text-sm
                                  transition-colors duration-150
                                  ${selectedEndHour === hour
                                    ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedEndHour === hour ? getSelectedBgStyle() : {}}
                              >
                                {hour.toString().padStart(2, '0')}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* End Minutes Column */}
                      <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                        <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                          Min
                        </div>
                        <div
                          ref={endMinuteScrollRef}
                          className="h-[220px] w-14 overflow-y-auto"
                        >
                          <div className="py-1">
                            {minutes.map((minute) => (
                              <button
                                key={minute}
                                type="button"
                                onClick={() => handleEndTimeChange('minute', minute)}
                                className={`
                                  w-full py-1.5 text-center text-sm
                                  transition-colors duration-150
                                  ${selectedEndMinute === minute
                                    ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                    : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                `}
                                style={selectedEndMinute === minute ? getSelectedBgStyle() : {}}
                              >
                                {minute.toString().padStart(2, '0')}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* End AM/PM Column */}
                      {!use24Hour && (
                        <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                          <div className="px-3 py-2 text-xs font-medium text-muted text-center border-b border-gray-200 dark:border-gray-700">
                            &nbsp;
                          </div>
                          <div
                            ref={endPeriodScrollRef}
                            className="h-[220px] w-14 overflow-y-auto"
                          >
                            <div className="py-1">
                              {(['AM', 'PM'] as const).map((period) => (
                                <button
                                  key={period}
                                  type="button"
                                  onClick={() => handleEndTimeChange('period', period)}
                                  className={`
                                    w-full py-1.5 text-center text-sm
                                    transition-colors duration-150
                                    ${selectedEndPeriod === period
                                      ? `text-white font-medium ${!themeEnabled ? 'bg-slate-700 dark:bg-slate-800' : ''}`
                                      : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'}
                                  `}
                                  style={selectedEndPeriod === period ? getSelectedBgStyle() : {}}
                                >
                                  {period}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer - hidden when autoApply is enabled */}
          {!autoApply && (
            <div className="flex justify-end gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md bg-panel px-3 py-2 text-sm font-semibold text-body shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className={`rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${!themeEnabled ? 'bg-slate-700 hover:bg-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700' : ''} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                style={getButtonBgStyle()}
                onMouseEnter={(e) => {
                  if (themeEnabled && 'buttonHoverBgStyle' in highlightStyles) {
                    e.currentTarget.style.backgroundColor = highlightStyles.buttonHoverBgStyle as string;
                  }
                }}
                onMouseLeave={(e) => {
                  if (themeEnabled && 'buttonBgStyle' in highlightStyles) {
                    e.currentTarget.style.backgroundColor = highlightStyles.buttonBgStyle as string;
                  }
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DateTimePicker;
